/**
 * Zod şemaları — form submit'lerinden önce payload'u validate et.
 * Kullanım:
 *   import { periodSchema, validate } from '../../utils/validation';
 *   const { ok, data, errors } = validate(periodSchema, payload);
 *   if (!ok) { setFieldErrors(errors); return; }
 */
import { z } from 'zod';

/* -------- yardımcı parser -------- */
export function validate(schema, payload) {
  const result = schema.safeParse(payload);
  if (result.success) return { ok: true, data: result.data, errors: {} };
  const errors = {};
  for (const issue of result.error.issues) {
    const path = issue.path.join('.') || '_';
    if (!errors[path]) errors[path] = issue.message;
  }
  return { ok: false, data: null, errors };
}

export function firstError(errors) {
  const keys = Object.keys(errors || {});
  return keys.length ? errors[keys[0]] : null;
}

/* -------- Dönem (tarih / yıl-ay) filtresi zorunluluğu — 2026-09 AS kararı -------- */
/**
 * Rule engine HİÇBİR AS tablosu için otomatik tarih/dönem filtresi UYGULAMAZ;
 * dönem kapsamı görevin KENDİ filtresinden gelir. Wizard, görev geçerlilik
 * tarihi yoksa bağlı dönemin başlangıç/bitişinden varsayılan üretir; admin
 * isterse değiştirir. Kural backend ile birebir
 * (``utils/source_validation.PERIOD_FILTER_RULES``): tablonun seçeneklerinden
 * (options) en az biri filtrede TAM olarak bulunmalı ve değerleri boş olmamalı.
 * İlk seçenek birincildir (eksik raporu ve kısayol için).
 */
export const PERIOD_FILTER_RULES = {
  as_uretim: { options: [['yil_no', 'ay_no'], ['uretim_tarihi']], label: 'aylık dönem bazlı beslenen tablodur' },
  as_prim: { options: [['yil_no', 'ay_no'], ['uretim_tarihi']], label: 'aylık dönem bazlı beslenen tablodur' },
  as_saglik: { options: [['yil_no', 'ay_no'], ['uretim_tarihi']], label: 'aylık dönem bazlı beslenen tablodur' },
  // yenileme: yil_no/ay_no kolonları var ama AS her satırda dolu göndermeyebilir → birincil tarih
  as_yenileme: { options: [['uretim_tarihi'], ['yil_no', 'ay_no']], label: 'tarih bazlı tablodur' },
  // grup_saglik: 2026-09-15 AS teyidi — aylık tam snapshot, YIL_NO/AY_NO her satırda
  as_grup_saglik: { options: [['yil_no', 'ay_no'], ['uretim_tarihi']], label: 'aylık dönem bazlı beslenen tablodur' },
  as_teklif: { options: [['uretim_tarihi']], label: 'tarih bazlı tablodur' },
  as_ziyaret: { options: [['ziyaret_tarihi']], label: 'tarih bazlı tablodur' },
  as_rota_hedefler: { options: [['yilno', 'ayno']], label: 'dönem (yıl/ay) bazlı hedef tablosudur' },
  as_hedef_musteri: { options: [['yilno', 'ayno']], label: 'dönem (yıl/ay) bazlı hedef tablosudur' },
};

const normTable = (t) => String(t || '').trim().toLowerCase();

export function periodFilterRule(table) {
  return PERIOD_FILTER_RULES[normTable(table)] || null;
}

export function hasPeriodFilterRule(table) {
  return Boolean(periodFilterRule(table));
}

const valueFilled = (v) => {
  if (v === null || v === undefined) return false;
  if (typeof v === 'string') return v.trim() !== '';
  if (Array.isArray(v)) return v.some(valueFilled);
  return true;
};

/**
 * Kaydedilecek filtre listesinden kolonu seçilmemiş satırları atar. Eklenip boş
 * bırakılan satır hiçbir şeyi filtreleyemez; motor boş kolon adını reddettiği
 * için görevin hesaplanmasını durdururdu (2026-09-21, görev 52).
 */
export function withoutColumnlessRows(rows) {
  return (Array.isArray(rows) ? rows : []).filter(
    (f) => String(f?.column ?? '').trim() !== '',
  );
}

const NO_VALUE_OPS = new Set(['IS NULL', 'IS NOT NULL']);

/** Kolonu seçilmiş ama değeri girilmemiş satır — yarım kalmış filtre. */
export function isFilterValueMissing(f) {
  if (String(f?.column ?? '').trim() === '') return false;
  if (NO_VALUE_OPS.has(String(f?.op || '').toUpperCase())) return false;
  return !valueFilled(f?.value);
}

/** Görev tipine göre backend'e gönderilen filtre listeleri (form alan adları). */
export function submittedFilterKeys(val) {
  if (val?.metric_type === 'MANUAL') return [];
  if (val?.metric_type === 'RATIO' && val?.is_cross_table_ratio) {
    return [
      'numerator_filters',
      'numerator_exclude_filters',
      'denominator_filters',
      'denominator_exclude_filters',
    ];
  }
  if (val?.metric_type === 'RANK') return ['filters'];
  return ['filters', 'exclude_filters'];
}

/** {kolon: en az bir satırda değer dolu mu} — kolon adı büyük/küçük harf duyarsız */
function filterColumnsWithValues(filters) {
  const out = {};
  for (const f of Array.isArray(filters) ? filters : []) {
    const col = String(f?.column || '').trim().toLowerCase();
    if (!col) continue;
    out[col] = Boolean(out[col]) || valueFilled(f?.value);
  }
  return out;
}

/**
 * Kural durumu: null (kural yok) ya da { ok, missing, empty }.
 *   ok      → bir seçenek tam ve değerleri dolu
 *   empty   → bir seçenek tam ama şu kolonların değeri boş
 *   missing → hiçbir seçenek tam değil; birincil seçeneğin eksik kolonları
 */
export function periodFilterStatus(table, filters) {
  const rule = periodFilterRule(table);
  if (!rule) return null;
  const cols = filterColumnsWithValues(filters);
  const present = rule.options.filter((o) => o.every((c) => c in cols));
  for (const o of present) {
    if (o.every((c) => cols[c])) return { ok: true, missing: [], empty: [] };
  }
  if (present.length) {
    return { ok: false, missing: [], empty: present[0].filter((c) => !cols[c]) };
  }
  return { ok: false, missing: rule.options[0].filter((c) => !(c in cols)), empty: [] };
}

/** Hiçbir seçenek sağlanmıyorsa birincil seçeneğin eksik kolonları; aksi halde []. */
export function missingPeriodFilters(table, filters) {
  const st = periodFilterStatus(table, filters);
  return st ? st.missing : [];
}

/** Seçenek kolonları var ama değeri boş olanlar; aksi halde []. */
export function emptyPeriodFilterValues(table, filters) {
  const st = periodFilterStatus(table, filters);
  return st ? st.empty : [];
}

/** İnsan-okur gereksinim: "YIL_NO ve AY_NO (veya URETIM_TARIHI)" */
export function periodFilterRequirement(table) {
  const rule = periodFilterRule(table);
  if (!rule) return '';
  const opts = rule.options.map((o) => o.map((c) => c.toUpperCase()).join(' ve '));
  let req = opts[0];
  if (opts.length > 1) req += ` (veya ${opts.slice(1).join(' / ')})`;
  return req;
}

export function periodFilterMessage(table, missing, empty = []) {
  const rule = periodFilterRule(table);
  const up = (arr) => (arr || []).map((c) => c.toUpperCase()).join(' ve ');
  if (!missing?.length && empty?.length) {
    return (
      `${normTable(table)}: ${up(empty)} dönem filtresinin değeri boş. ` +
      'Temel Bilgi sekmesinde görev tarihi ya da dönem seçildiğinde otomatik dolar; elle de girebilirsiniz.'
    );
  }
  const eksik = up(missing);
  return (
    `${normTable(table)} ${rule?.label || 'dönem bazlı tablodur'}: ` +
    `${periodFilterRequirement(table)} filtresi zorunlu${eksik ? ` (eksik: ${eksik})` : ''}. ` +
    'Dönem filtresi olmadan görev tablodaki TÜM dönemleri toplar.'
  );
}

/* -------- Period -------- */

export const periodSchema = z
  .object({
    name: z.string().trim().min(3, 'En az 3 karakter'),
    start_date: z.string().min(1, 'Başlangıç tarihi zorunlu'),
    end_date: z.string().min(1, 'Bitiş tarihi zorunlu'),
    description: z.string().optional(),
    // Hedefleme — "Kullanıcı Tipi (Segment)" UI etiketiyle gösterilir ama değerler CH
    // ``segments`` tablosundan (tenant-filtered) gelir. Boş liste =
    // dönem tenant geneline açık.
    segment_ids: z.array(z.string().trim().min(1)).optional().default([]),
    mudurluk_kodlari: z.array(z.string().trim().min(1)).optional().nullable(),
    min_total_points: z.coerce.number().min(0).optional().default(0),
    // max_total_points OPSİYONEL — boş bırakılabilir (üst sınır yok demektir).
    // z.coerce.number() boş string için 0 üretir ve .positive() bunu reddeder
    // → kullanıcı silmeye çalışınca "Number must be greater than 0" hatası alır.
    // preprocess ile "", null, undefined → null'a çevrilir; gerçek sayı ise validate edilir.
    max_total_points: z
      .preprocess(
        (v) => (v === '' || v == null ? null : v),
        z.coerce.number().int().positive().nullable(),
      )
      .optional(),
    // Puan → ASPara oranı (1 puan = 0,5 ASPara için 0.5). 0 ⇒ devre dışı.
    aspara_per_point: z.coerce.number().min(0).optional().default(0),
  })
  .refine((v) => v.start_date <= v.end_date, {
    message: 'Bitiş tarihi, başlangıçtan önce olamaz',
    path: ['end_date'],
  })
  .refine(
    (v) => !v.max_total_points || Number(v.max_total_points) >= Number(v.min_total_points || 0),
    {
      message: 'Max. puan, min. puandan küçük olamaz',
      path: ['max_total_points'],
    },
  );

/* -------- Task (3-adım wizard için tek birleşik şema) -------- */
const tierSchema = z.object({
  // Wizard flat state 'GTE/GT/EQ/LT/LTE' kullanıyor, backend de bu formu kabul eder
  threshold_operator: z.enum(['GTE', 'GT', 'EQ', 'LT', 'LTE']),
  threshold_value: z.coerce.number().finite('Sayısal değer girin'),
  points: z.coerce.number().min(0, 'Puan 0 veya pozitif olmalı'),
  name: z.string().trim().max(64).optional().nullable(),
});

/**
 * Bonus koşulu (analiz §4.5 — örn. DASK satışı +10p). Tüm alanlar opsiyonel;
 * ya tümü dolu ya tümü boş olmalı (backend JSONB'ye aynen yazar).
 */
const bonusConditionSchema = z
  .object({
    column: z.string().trim().optional().nullable(),
    op: z.string().trim().optional().nullable(),
    value: z.any().optional(),
    bonus_points: z.coerce.number().min(0).optional().default(0),
  })
  .optional()
  .nullable()
  .superRefine((val, ctx) => {
    if (!val) return;
    const any = val.column || val.op || val.value !== undefined || val.bonus_points;
    const allKeys = Boolean(val.column) && Boolean(val.op);
    if (any && !allKeys) {
      ctx.addIssue({
        code: 'custom',
        path: ['column'],
        message: 'Bonus için kolon ve operatör birlikte girilmeli',
      });
    }
  });

// COUNT için sadece tablo zorunlu — backend `count()` döner, kolon kullanmaz.
// (`metric_builder.py:METRIC_TYPE_REQUIREMENTS["COUNT"] = "none"`)
const sourceCount = z.object({
  ch_table: z.string().min(1, 'Kaynak tablo seçin'),
});

// SUM (ve diğer aggregate'ler) için kolon zorunlu — sayısal değer toplanacak.
const sourceSum = z.object({
  ch_table: z.string().min(1, 'Kaynak tablo seçin'),
  metric_column: z.string().min(1, 'Metrik sütunu seçin'),
});

const sourceRatio = z.object({
  numerator: z.object({
    table: z.string().min(1, 'Pay tablosu seçin'),
    column: z.string().min(1, 'Pay sütunu seçin'),
  }),
  denominator: z.object({
    table: z.string().min(1, 'Payda tablosu seçin'),
    column: z.string().min(1, 'Payda sütunu seçin'),
  }),
});

const sourceRank = z.object({
  ch_table: z.string().min(1, 'Kaynak tablo seçin'),
  rank_by: z.string().min(1, 'Sıralama kriteri seçin'),
});

export const taskSchema = z
  .object({
    name: z.string().trim().min(3, 'En az 3 karakter'),
    description: z.string().optional(),
    game_period_id: z.coerce.number().int().positive('Dönem seçin'),
    metric_type: z.enum(['COUNT', 'SUM', 'RATIO', 'RANK', 'MANUAL']),
    // max_points artık iki kaynaktan gelir:
    //   - MANUAL: admin Puanlama sekmesinde elle girer (>0 zorunlu).
    //   - Diğer tipler: tier'lardan auto-derive (max(tier.points)). UI'da
    //     ayrı input yok; submit handler türetir. Şemada non-negative kabul.
    max_points: z.coerce.number().min(0, 'Negatif olamaz'),
    is_joker: z.boolean().optional().default(false),
    // cancellation_policy artık görev oluştururken değil, iptal anında seçilir
    cancellation_policy: z.enum(['FULL_POINTS', 'CANCEL_REVOKE', 'REPLACE_RECALC']).optional(),
    source: z.any().optional(),
    tiers: z.array(tierSchema),
  })
  .superRefine((val, ctx) => {
    // Manuel görevde kademe zorunlu değil
    if (val.metric_type !== 'MANUAL' && val.tiers.length === 0) {
      ctx.addIssue({ code: 'custom', path: ['tiers'], message: 'En az bir kademe tanımlayın' });
    }
    // MANUAL için max_points elle giriliyor, > 0 olmalı
    if (val.metric_type === 'MANUAL' && (!val.max_points || val.max_points <= 0)) {
      ctx.addIssue({
        code: 'custom',
        path: ['max_points'],
        message: 'Manuel görevde Max Puan (cap) zorunlu — admin elle giriyor.',
      });
    }
    // Metrik tipine göre source zorunluluğu
    if (val.metric_type === 'COUNT') {
      // Sadece ch_table zorunlu — kolon kullanılmıyor
      const r = sourceCount.safeParse(val.source || {});
      if (!r.success)
        for (const i of r.error.issues)
          ctx.addIssue({ code: 'custom', path: ['source', ...i.path], message: i.message });
    } else if (val.metric_type === 'SUM') {
      const r = sourceSum.safeParse(val.source || {});
      if (!r.success)
        for (const i of r.error.issues)
          ctx.addIssue({ code: 'custom', path: ['source', ...i.path], message: i.message });
    } else if (val.metric_type === 'RATIO') {
      const r = sourceRatio.safeParse(val.source || {});
      if (!r.success)
        for (const i of r.error.issues)
          ctx.addIssue({ code: 'custom', path: ['source', ...i.path], message: i.message });
    } else if (val.metric_type === 'RANK') {
      const r = sourceRank.safeParse(val.source || {});
      if (!r.success)
        for (const i of r.error.issues)
          ctx.addIssue({ code: 'custom', path: ['source', ...i.path], message: i.message });
    }
    // NOT: Eski "Kademe puanları max_points'i aşmasın" kontrolü kaldırıldı —
    // max_points artık non-MANUAL tiplerde tier'lardan otomatik türetiliyor,
    // tutarsızlık imkansız.
    // Kademe eşikleri sıralı (operatör >= ise büyükten küçüğe)
    if (val.tiers[0]?.threshold_operator === '>=') {
      for (let i = 1; i < val.tiers.length; i++) {
        if (Number(val.tiers[i].threshold_value) > Number(val.tiers[i - 1].threshold_value)) {
          ctx.addIssue({
            code: 'custom',
            path: ['tiers', i, 'threshold_value'],
            message: 'Kademeler büyükten küçüğe sıralı olmalı',
          });
        }
      }
    }
  });

/**
 * Wizard adım-bazlı doğrulama — flat state şemasıyla (source_table, metric_column,
 * formula_numerator, formula_denominator) çalışır.
 */
export const taskWizardStepSchemas = {
  0: z
    .object({
      name: z.string().trim().min(3, 'En az 3 karakter'),
      game_period_id: z.union([z.string().min(1), z.number().int().positive()]),
      // max_points artık Temel Bilgi'de değil — Puanlama sekmesinde validate
      // ediliyor (MANUAL için elle, non-MANUAL için tier'dan auto-derive).
      // cancellation_policy iptal anında seçilir, form validasyonunda gerekmez
      cancellation_policy: z.enum(['FULL_POINTS', 'CANCEL_REVOKE', 'REPLACE_RECALC']).optional(),
      // Analiz §4.2 ek alanları — hepsi opsiyonel
      sort_order: z.coerce.number().int().min(0).optional().default(0),
      effective_start_date: z.string().optional().nullable(),
      effective_end_date: z.string().optional().nullable(),
      bonus_condition: bonusConditionSchema,
      // Görev-seviyesi hedefleme — segment_ids ("Kullanıcı Tipi (Segment)" etiketi)
      // ve mudurluk_kodlari opsiyonel; boş = dönemden devral. Backend alt-küme
      // kuralını sunucu tarafında da doğrular.
      segment_ids: z.array(z.string().min(1)).optional().default([]),
      mudurluk_kodlari: z.array(z.string().min(1)).optional().default([]),
    })
    .superRefine((val, ctx) => {
      if (
        val.effective_start_date &&
        val.effective_end_date &&
        String(val.effective_start_date) > String(val.effective_end_date)
      ) {
        ctx.addIssue({
          code: 'custom',
          path: ['effective_end_date'],
          message: 'Bitiş tarihi, başlangıçtan önce olamaz',
        });
      }
    }),
  1: z.object({
    metric_type: z.enum(['COUNT', 'SUM', 'RATIO', 'RANK', 'MANUAL']),
    source_table: z.string().optional(),
    metric_column: z.string().optional(),
    formula_numerator: z.string().optional(),
    formula_denominator: z.string().optional(),
    // Aylık dönem (partition) tablosu kontrolü için — bkz. missingPeriodFilters
    is_cross_table_ratio: z.boolean().optional(),
    formula_numerator_table: z.string().optional().nullable(),
    formula_denominator_table: z.string().optional().nullable(),
    filters: z.array(z.any()).optional(),
    exclude_filters: z.array(z.any()).optional(),
    numerator_filters: z.array(z.any()).optional(),
    numerator_exclude_filters: z.array(z.any()).optional(),
    denominator_filters: z.array(z.any()).optional(),
    denominator_exclude_filters: z.array(z.any()).optional(),
  }).superRefine((val, ctx) => {
    if (val.metric_type === 'MANUAL') return;
    // Kolonu seçilmiş ama değeri boş satır sessizce atılmaz: yarım kalan filtre
    // görevi fark edilmeden daha geniş veriyi saydırır. Kolonu boş satırlar ise
    // kaydederken atılır (withoutColumnlessRows).
    for (const key of submittedFilterKeys(val)) {
      const missing = (Array.isArray(val[key]) ? val[key] : []).filter(isFilterValueMissing);
      if (missing.length) {
        ctx.addIssue({
          code: 'custom',
          path: [key],
          message: `${missing.length} filtre satırında değer boş: değer girin ya da satırı silin.`,
        });
      }
    }
    if (!val.source_table)
      ctx.addIssue({ code: 'custom', path: ['source_table'], message: 'Kaynak tablo seçin' });
    // Dönem filtresi kuralı (PERIOD_FILTER_RULES): tarihli her AS tablosunda
    // dönem filtresi zorunlu ve değeri dolu olmalı (motor otomatik filtre uygulamaz).
    // Çapraz RATIO'da her bacak kendi tablosu/filtresiyle ayrı kontrol edilir
    // (backend'e üst-seviye filtre gitmez); diğer modlarda üst-seviye filtreler
    // tüm bacaklara uygulanır → tek kontrol yeter.
    const isCross = val.metric_type === 'RATIO' && Boolean(val.is_cross_table_ratio);
    const legs = isCross
      ? [
          ['numerator_filters', val.formula_numerator_table, val.numerator_filters],
          ['denominator_filters', val.formula_denominator_table, val.denominator_filters],
        ]
      : [['filters', val.source_table, val.filters]];
    for (const [path, table, filters] of legs) {
      const st = periodFilterStatus(table, filters);
      if (st && !st.ok) {
        ctx.addIssue({
          code: 'custom',
          path: [path],
          message: periodFilterMessage(table, st.missing, st.empty),
        });
      }
    }
    if (val.metric_type === 'RATIO') {
      if (!val.formula_numerator)
        ctx.addIssue({ code: 'custom', path: ['formula_numerator'], message: 'Pay kolonu seçin' });
      if (!val.formula_denominator)
        ctx.addIssue({ code: 'custom', path: ['formula_denominator'], message: 'Payda kolonu seçin' });
    } else if (['SUM', 'AVG', 'MIN', 'MAX', 'COUNT_DISTINCT', 'RANK'].includes(val.metric_type)) {
      // COUNT ve MANUAL bu listede DEĞİL — kolon gerektirmezler:
      //   * COUNT  → backend `count()` döner, kolon parametresi kullanılmaz
      //              (`metric_builder.py:METRIC_TYPE_REQUIREMENTS["COUNT"] = "none"`).
      //   * MANUAL → puanlar admin tarafından elle girilir.
      // Diğer metric_type'lar için kolon zorunlu (sayısal/distinct/rank kriteri).
      if (!val.metric_column)
        ctx.addIssue({ code: 'custom', path: ['metric_column'], message: 'Metrik kolonu seçin' });
    }
  }),
  2: z.object({
    metric_type: z.enum(['COUNT', 'SUM', 'RATIO', 'RANK', 'MANUAL']).optional(),
    tiers: z.array(tierSchema),
    // MANUAL: elle girilir, > 0 zorunlu (cap olarak kullanılır).
    // Diğer tipler: tier'dan auto-derive, validation gevşek (>= 0).
    max_points: z.coerce.number().min(0),
  }).superRefine((val, ctx) => {
    // Manuel görevde kademe zorunlu değil; MANUAL'da max_points zorunlu.
    if (val.metric_type === 'MANUAL') {
      if (!val.max_points || val.max_points <= 0) {
        ctx.addIssue({
          code: 'custom',
          path: ['max_points'],
          message: 'Manuel görevde Max Puan (cap) sıfırdan büyük olmalı.',
        });
      }
    } else {
      // Tier-bazlı tipler için en az bir kademe gerekli; max_points
      // submit aşamasında tier.points max'ından türetildiği için ayrı
      // validation yok.
      if (val.tiers.length === 0) {
        ctx.addIssue({ code: 'custom', path: ['tiers'], message: 'En az bir kademe tanımlayın' });
      }
    }
  }),
};

/* -------- System Parameter (type-aware value) -------- */
const baseParamSchema = z.object({
  param_key: z
    .string()
    .trim()
    .min(2, 'En az 2 karakter')
    .regex(/^[a-z][a-z0-9_]*$/i, 'Sadece harf/rakam/alt tire (harfle başla)'),
  label: z.string().trim().min(2, 'En az 2 karakter'),
  param_type: z.enum(['STRING', 'NUMBER', 'BOOLEAN', 'LIST']),
  // Refactor 2026-04: kategori UI'dan kaldirildi, backend 'FILTRE' sabit aliyor.
  // Validation'da artik category yok — handleSave hardcode ediyor.
  description: z.string().optional(),
  param_value: z.any(),
});

export const parameterSchema = baseParamSchema.superRefine((val, ctx) => {
  // Tipe göre değer doğrulama
  const v = val.param_value;
  if (v === '' || v === null || v === undefined) {
    ctx.addIssue({ code: 'custom', path: ['param_value'], message: 'Değer zorunlu' });
    return;
  }
  if (val.param_type === 'NUMBER') {
    if (Number.isNaN(Number(v)))
      ctx.addIssue({ code: 'custom', path: ['param_value'], message: 'Sayısal değer girin' });
  } else if (val.param_type === 'LIST') {
    const arr = Array.isArray(v) ? v : String(v).split(',').map((x) => x.trim()).filter(Boolean);
    if (arr.length === 0)
      ctx.addIssue({ code: 'custom', path: ['param_value'], message: 'En az bir öğe girin' });
  } else if (val.param_type === 'BOOLEAN') {
    if (typeof v !== 'boolean' && !['true', 'false'].includes(String(v)))
      ctx.addIssue({ code: 'custom', path: ['param_value'], message: 'Evet/Hayır seçin' });
  }
});
